import { useState, useCallback, useEffect, useMemo } from 'react';
import { useDrag } from '@use-gesture/react';

/**
 * Custom hook for managing photo selection state and logic
 * @param {boolean} selectionMode - Whether selection mode is active
 * @param {Array} photos - Array of photo filenames
 * @returns {Object} Selection state and functions
 */
const NO_PHOTOS = [];

export const usePhotoSelection = (selectionMode, photos = NO_PHOTOS) => {
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [lastSelectedPhoto, setLastSelectedPhoto] = useState(null);
  const [isDraggingSelection, setIsDraggingSelection] = useState(false);
  const [selectionStartState, setSelectionStartState] = useState(null);

  const photoSet = useMemo(() => new Set(photos), [photos]);

  // Keep the selection a subset of the current list. A filter change or a
  // refetch that drops items must not leave hidden ids counted or downloaded.
  // Returning `prev` when nothing was dropped keeps this from re-rendering.
  // In-flight downloads snapshot their ids up front, so pruning never changes
  // what a running download requests; failed ids stay selected while listed.
  useEffect(() => {
    setSelectedPhotos(prev => {
      let changed = false;
      const next = new Set();
      prev.forEach(filename => {
        if (photoSet.has(filename)) next.add(filename);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [photoSet]);

  /**
   * Clear all selections
   */
  const clearSelections = useCallback(() => {
    setSelectedPhotos(new Set());
    setIsDraggingSelection(false);
    setSelectionStartState(null);
    setLastSelectedPhoto(null);
  }, []);

  const removeSelections = useCallback((filenames) => {
    const completed = new Set(filenames || []);
    if (completed.size === 0) return;

    setSelectedPhotos(prev => {
      const next = new Set(prev);
      completed.forEach(filename => next.delete(filename));
      return next;
    });
    setLastSelectedPhoto(prev => completed.has(prev) ? null : prev);
  }, []);

  /**
   * Toggle selection of a specific photo
   * @param {string} filename - The photo filename to toggle
   */
  const togglePhotoSelection = useCallback((filename) => {
    if (!selectionMode) return;

    setSelectedPhotos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(filename)) {
        newSet.delete(filename);
      } else {
        newSet.add(filename);
      }
      return newSet;
    });
    setLastSelectedPhoto(filename);
  }, [selectionMode]);

  // Derived rather than stored: loading another page or unticking a photo
  // must immediately flip the toggle back to "select".
  const selectAll = useMemo(
    () => photos.length > 0 && photos.every((filename) => selectedPhotos.has(filename)),
    [photos, selectedPhotos]
  );

  /**
   * Select every loaded photo, or clear the selection when all are selected
   */
  const toggleSelectAll = useCallback(() => {
    if (selectAll) {
      setSelectedPhotos(new Set());
    } else {
      setSelectedPhotos(new Set(photos));
    }
  }, [selectAll, photos]);

  /**
   * Gesture binding for drag selection
   */
  const bindSelectionGesture = useDrag(
    ({ event, first, last, movement: [mx, my], direction: [dx, dy], distance, cancel }) => {
      if (!selectionMode) return;

      if (first) {
        // Get the initial photo element
        const target = event.target.closest('[data-photo-filename]');
        if (target) {
          const filename = target.dataset.photoFilename;
          // Pending-upload tiles render but are not selectable media.
          if (filename && photoSet.has(filename)) {
            setIsDraggingSelection(true);
            setSelectionStartState(filename);
            setLastSelectedPhoto(filename);

            // Determine if we're starting with selection or deselection
            const isCurrentlySelected = selectedPhotos.has(filename);
            setSelectionStartState({ filename, action: isCurrentlySelected ? 'deselect' : 'select' });

            // Toggle the initial photo
            togglePhotoSelection(filename);
          }
        }
      } else if (isDraggingSelection) {
        // Find the photo element under the current touch point
        const element = document.elementFromPoint(event.clientX, event.clientY);
        if (element) {
          const photoContainer = element.closest('[data-photo-filename]');
          if (photoContainer) {
            const filename = photoContainer.dataset.photoFilename;
            if (filename && filename !== lastSelectedPhoto && photoSet.has(filename)) {
              setLastSelectedPhoto(filename);

              // Apply the same action (select/deselect) as the initial photo
              if (selectionStartState && selectionStartState.action) {
                const isCurrentlySelected = selectedPhotos.has(filename);
                const shouldSelect = selectionStartState.action === 'select';

                if (shouldSelect && !isCurrentlySelected) {
                  // Add to selection
                  setSelectedPhotos(prev => new Set([...prev, filename]));
                } else if (!shouldSelect && isCurrentlySelected) {
                  // Remove from selection
                  setSelectedPhotos(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(filename);
                    return newSet;
                  });
                }
              }
            }
          }
        }
      }

      if (last) {
        // End of gesture
        setIsDraggingSelection(false);
        setSelectionStartState(null);
        setLastSelectedPhoto(null);
      }
    },
    {
      // Configuration for better touch handling
      filterTaps: true,
      threshold: 5, // Minimum distance to start drag
      preventDefault: true,
      eventOptions: { passive: false }
    }
  );

  return {
    // State
    selectedPhotos,
    selectAll,
    lastSelectedPhoto,
    isDraggingSelection,
    selectionStartState,

    // Functions
    clearSelections,
    removeSelections,
    togglePhotoSelection,
    toggleSelectAll,
    bindSelectionGesture
  };
};
